/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { InspectionReportContext } from "stores/InspectionReportStore";
import {
  Modal,
  Card,
  FlexView,
  Icon,
  Button,
  LoadingOverlay,
} from "components/common";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import LacunaWebPKI from "web-pki";

const ApprovalModal = ({ isOpen, onOutsideClick }) => {
  const { t } = useTranslation();
  const { approveReport, getSignToken, signReport } = useContext(
    InspectionReportContext
  );
  const [loading, setLoading] = useState(false);
  const [pkiMessage, setPkiMessage] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const pki = new LacunaWebPKI();
  const history = useHistory();

  useEffect(() => {
    pki.init({
      ready: onWebPkiReady,
      notInstalled: onWebPkiNotInstalled,
      defaultFail: onWebPkiFail,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onWebPkiReady = () => {
    onLoadCertificates();
  };

  // The notInstalled callback
  const onWebPkiNotInstalled = (status, message) => {
    setPkiMessage("notInstalled");
  };

  // The default fail callback
  const onWebPkiFail = (ex) => {
    setPkiMessage("fail");
  };

  const onInstallPkiClick = () => {
    pki.redirectToInstallPage();
  };

  const onLoadCertificates = () => {
    setLoading(true);
    setCertificates([]);
    pki
      .listCertificates()
      .success((certs) => {
        setLoading(false);
        setCertificates(certs);
      })
      .fail((e) => setLoading(false));
  };

  const onSign = useCallback(
    (cert) => async () => {
      setLoading(true);
      try {
        const token = await getSignToken();
        const signData = await signWithRestPki(cert, token);
        const signResult = await signReport(signData);
        if (signResult.signed) {
          await approveReport();
          toast.success(t("inspectionReportApproval.operationSuccess"));
          history.replace("/inspection-report");
        } else {
          toast.error(t("inspectionReportApproval.signError"));
        }
      } catch (error) {
        console.debug(error);
        toast.error(error.errorMessage);
      }
      setLoading(false);
      onOutsideClick();
    },
    []
  );

  const signWithRestPki = (cert, token) =>
    new Promise((resolve, reject) => {
      pki
        .signWithRestPki({
          token: token,
          thumbprint: cert.thumbprint,
        })
        .success((data) => {
          resolve(data);
        })
        .fail((e) => {
          reject(e);
        });
    });

  return (
    <Modal isOpen={isOpen} onOutsideClick={onOutsideClick}>
      <Card padding="16px" maxWidth="800px" width="100vw">
        <FlexView
          fontSize="subtitle"
          fontWeight="bold"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          alignSelf="stretch"
        >
          <span>{t("inspectionReportApproval.certificates.select")}</span>
          <Icon
            name="cross"
            color="error"
            width="24px"
            height="24px"
            onClick={onOutsideClick}
          />
        </FlexView>
        <FlexView
          alignSelf="stretch"
          margin="8px 0px 0px"
          height="310px"
          style={{ overflow: "auto" }}
        >
          {certificates.length > 0 ? (
            certificates.map((x) => {
              return (
                <Button
                  key={x.subjectName}
                  margin="0px 0px 8px"
                  color="white"
                  backgroundColor="success"
                  width="100%"
                  height="39px"
                  onClick={onSign(x)}
                >
                  {x.subjectName + " - (" + x.issuerName + ")"}
                </Button>
              );
            })
          ) : (
            <FlexView
              alignItems="center"
              justifyContent="center"
              color="error"
              width="100%"
              height="100%"
            >
              {!pkiMessage ? (
                t("inspectionReportApproval.certificates.notFound")
              ) : pkiMessage === "fail" ? (
                t("inspectionReportApproval.certificates.fail")
              ) : (
                <Button
                  backgroundColor="success"
                  color="white"
                  height="39px"
                  onClick={onInstallPkiClick}
                >
                  {t("inspectionReportApproval.certificates.install")}
                </Button>
              )}
            </FlexView>
          )}
        </FlexView>
        <FlexView
          flexDirection="row"
          justifyContent="flex-end"
          alignItems="center"
          alignSelf="stretch"
        >
          <Button
            disabled={pkiMessage}
            isLoading={loading}
            margin="0px 0px 8px"
            backgroundColor="primary"
            color="white"
            height="39px"
            onClick={onLoadCertificates}
          >
            {t("inspectionReportApproval.certificates.reload")}
          </Button>
        </FlexView>
      </Card>
      <LoadingOverlay visible={loading} />
    </Modal>
  );
};

export default ApprovalModal;
